import React, { useState } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useBatchCreateTransactions } from '@/hooks/useTransactions';
import { Plus, Trash2, Loader2, TrendingUp, TrendingDown, CheckCircle2, FolderPlus, CalendarIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { format, parseISO } from 'date-fns';
import { toDateString } from '@/lib/format';
import type { Category } from '@/lib/api';

interface TransactionRow {
  id: string;
  type: 'income' | 'expense' | '';
  amount: string;
  category_id: string;
  date: string;
  description: string;
}

const today = () => new Date().toISOString().split('T')[0];

const emptyRow = (): TransactionRow => ({
  id: crypto.randomUUID(),
  type: '',
  amount: '',
  category_id: '',
  date: today(),
  description: '',
});

export interface BatchTransactionEntryProps {
  categories: Category[];
  onClose: () => void;
}

export const BatchTransactionEntry: React.FC<BatchTransactionEntryProps> = ({ categories, onClose }) => {
  const [rows, setRows] = useState<TransactionRow[]>([emptyRow()]);
  const batchCreate = useBatchCreateTransactions();

  const updateRow = (id: string, field: keyof TransactionRow, value: string) => {
    setRows((prev) =>
      prev.map((r) => {
        if (r.id !== id) return r;
        const updated = { ...r, [field]: value };
        if (field === 'type') updated.category_id = '';
        return updated;
      })
    );
  };

  const addRow = () => setRows((prev) => [...prev, emptyRow()]);
  const removeRow = (id: string) => setRows((prev) => prev.filter((r) => r.id !== id));

  const validRows = rows.filter(
    (r) => r.type && r.amount && parseFloat(r.amount) > 0 && r.category_id && r.date && r.description.trim()
  );

  const handleSubmitAll = () => {
    if (validRows.length === 0) return;

    batchCreate.mutate(
      validRows.map((row) => ({
        amount: parseFloat(row.amount),
        category_id: row.category_id,
        date: row.date,
        description: row.description,
        type: row.type as 'income' | 'expense',
      })),
      { onSuccess: onClose },
    );
  };

  if (categories.length === 0) {
    return (
      <div className="bg-card border border-border rounded-xl p-8 text-center animate-slide-down">
        <div className="mx-auto w-10 h-10 rounded-full bg-muted flex items-center justify-center mb-3">
          <FolderPlus className="h-5 w-5 text-muted-foreground" />
        </div>
        <h3 className="font-semibold text-foreground mb-1">Nenhuma categoria ainda</h3>
        <p className="text-sm text-muted-foreground mb-4 max-w-xs mx-auto">
          Você precisa criar pelo menos uma categoria antes de registrar transações.
        </p>
        <Link
          to="/categories"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-primary text-primary-foreground text-sm font-semibold hover:opacity-90 transition-opacity"
          onClick={onClose}
        >
          <FolderPlus className="h-4 w-4" />
          Criar Categoria
        </Link>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-xl overflow-hidden animate-slide-down">
      {/* Panel header */}
      <div className="flex items-center justify-between px-5 py-4 border-b border-border bg-muted/30">
        <div>
          <h3 className="font-semibold text-foreground text-sm">Lançamento em Lote</h3>
          <p className="text-xs text-muted-foreground mt-0.5">
            {validRows.length > 0
              ? `${validRows.length} de ${rows.length} ${rows.length === 1 ? 'linha válida' : 'linhas válidas'}`
              : `${rows.length} ${rows.length === 1 ? 'linha' : 'linhas'}`}
          </p>
        </div>
        <button
          onClick={onClose}
          className="text-muted-foreground hover:text-foreground text-xs hover:bg-muted px-2.5 py-1.5 rounded-md transition-all"
        >
          Fechar
        </button>
      </div>

      {/* Rows */}
      <div className="divide-y divide-border">
        {rows.map((row, idx) => {
          const filteredCategories = row.type ? categories.filter((c) => c.type === row.type) : [];
          const isRowValid = row.type && row.amount && parseFloat(row.amount) > 0 && row.category_id && row.date && row.description.trim();

          return (
            <div key={row.id} className="px-5 py-4 animate-fade-in">
              {/* Row number + valid indicator */}
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs font-medium text-muted-foreground">Lançamento {idx + 1}</span>
                <div className="flex items-center gap-2">
                  {isRowValid && <CheckCircle2 className="h-3.5 w-3.5 text-muted-foreground" />}
                  {rows.length > 1 && (
                    <button
                      onClick={() => removeRow(row.id)}
                      className="text-muted-foreground/60 hover:text-destructive transition-colors"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
              </div>

              {/* Mobile: single col stack. sm: 2-col pairing. md: full 6-col row. */}
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-6 gap-3">
                {/* Type — full width on mobile/sm */}
                <div className="sm:col-span-2 md:col-span-1">
                  <Label className="text-xs text-muted-foreground mb-1.5 block">Tipo</Label>
                  <div className="flex gap-1.5">
                    <button
                      type="button"
                      onClick={() => updateRow(row.id, 'type', 'income')}
                      className={cn(
                        'flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-md text-xs font-medium transition-all border',
                        row.type === 'income'
                          ? 'bg-income/15 text-income border-income/30'
                          : 'bg-muted/50 text-muted-foreground border-border hover:border-income/30 hover:text-income/70'
                      )}
                    >
                      <TrendingUp className="h-3 w-3 shrink-0" />
                      Receita
                    </button>
                    <button
                      type="button"
                      onClick={() => updateRow(row.id, 'type', 'expense')}
                      className={cn(
                        'flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-md text-xs font-medium transition-all border',
                        row.type === 'expense'
                          ? 'bg-expense/15 text-expense border-expense/30'
                          : 'bg-muted/50 text-muted-foreground border-border hover:border-expense/30 hover:text-expense/70'
                      )}
                    >
                      <TrendingDown className="h-3 w-3 shrink-0" />
                      Despesa
                    </button>
                  </div>
                </div>

                {/* Amount — pairs with Date on sm */}
                <div className="md:col-span-1">
                  <Label className="text-xs text-muted-foreground mb-1.5 block">Valor (R$)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="0,00"
                    value={row.amount}
                    onChange={(e) => updateRow(row.id, 'amount', e.target.value)}
                    className="h-9 text-sm"
                  />
                </div>

                {/* Date — sits next to Amount on sm (reordered before Category) */}
                <div className="md:col-span-1">
                  <Label className="text-xs text-muted-foreground mb-1.5 block">Data</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className="h-9 w-full justify-start text-left text-sm font-normal"
                      >
                        <CalendarIcon className="mr-2 h-3.5 w-3.5" />
                        {row.date ? format(parseISO(row.date), 'dd/MM/yyyy') : 'Data'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={row.date ? parseISO(row.date) : undefined}
                        onSelect={(date) => date && updateRow(row.id, 'date', toDateString(date))}
                        initialFocus
                        className="pointer-events-auto"
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                {/* Category — full width on sm */}
                <div className="sm:col-span-2 md:col-span-1">
                  <Label className="text-xs text-muted-foreground mb-1.5 block">Categoria</Label>
                  <Select
                    value={row.category_id}
                    onValueChange={(v) => updateRow(row.id, 'category_id', v)}
                    disabled={!row.type}
                  >
                    <SelectTrigger className="h-9 text-sm">
                      <SelectValue placeholder={row.type ? 'Categoria' : '— selecione o tipo'} />
                    </SelectTrigger>
                    <SelectContent>
                      {filteredCategories.length === 0 ? (
                        <div className="px-2 py-3 text-center">
                          <p className="text-xs text-muted-foreground">Sem categorias de {row.type === 'income' ? 'receita' : 'despesa'}</p>
                        </div>
                      ) : (
                        filteredCategories.map((cat) => (
                          <SelectItem key={cat.id} value={cat.id} className="text-sm">{cat.name}</SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                </div>

                {/* Description — full width on sm */}
                <div className="sm:col-span-2 md:col-span-2">
                  <Label className="text-xs text-muted-foreground mb-1.5 block">Descrição</Label>
                  <Input
                    placeholder="Para que foi?"
                    value={row.description}
                    onChange={(e) => updateRow(row.id, 'description', e.target.value)}
                    className="h-9 text-sm"
                  />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between px-5 py-4 border-t border-border bg-muted/20">
        <button
          type="button"
          onClick={addRow}
          disabled={batchCreate.isPending}
          className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground hover:bg-muted px-3 py-1.5 rounded-md transition-all disabled:opacity-50"
        >
          <Plus className="h-3.5 w-3.5" />
          Adicionar linha
        </button>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={batchCreate.isPending}
            className="px-4 py-2 rounded-md text-sm text-muted-foreground hover:bg-muted transition-all disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleSubmitAll}
            disabled={batchCreate.isPending || validRows.length === 0}
            className="flex items-center gap-2 px-4 py-2 rounded-md bg-primary text-primary-foreground text-sm font-semibold hover:opacity-90 transition-opacity disabled:opacity-50"
          >
            {batchCreate.isPending ? (
              <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Registrando...</>
            ) : (
              <>Lançar {validRows.length > 0 ? `${validRows.length} ` : ''}{validRows.length === 1 ? 'transação' : 'transações'}</>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};
